/**
 * A job is one visit to one location, billed once; a recurring job is the
 * rule that keeps creating them (docs/majik/DATA-ARCHITECTURE-SPEC.md → Jobs
 * & recurring jobs). Pure: no React, no router, no vendor SDK.
 */
import { z } from 'zod'
import { cadences } from '../shared/cadence.ts'
import { patchOf } from '../shared/patch.ts'
import { recordIdSchema } from '../shared/record-id.ts'

/**
 * What a job's state is. People see Draft, Scheduled or Completed — a billed
 * job is Completed, and where its money is shows in the Billing column
 * (docs/majik/PROTOTYPE-DECISIONS.md → Jobs). Cancelled jobs leave the
 * default list (docs/DECISIONS.md D-005). A draft is not yet on: it never
 * completes by itself, so it can't reach Ready to bill (D-037; Monday has a
 * few).
 */
export const jobStatuses = ['draft', 'scheduled', 'completed', 'billed', 'cancelled'] as const
export type JobStatus = (typeof jobStatuses)[number]

/** The statuses a person sees, filters by and sorts by. */
export const shownStatuses = ['draft', 'scheduled', 'completed', 'cancelled'] as const
export type ShownStatus = (typeof shownStatuses)[number]

export function shownStatus(status: JobStatus): ShownStatus {
  return status === 'billed' ? 'completed' : status
}

export const shownStatusLabels: Record<ShownStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

const clock = z.string().regex(/^\d{2}:\d{2}$/, 'A time is HH:MM.')

export const jobCleanerSchema = z.object({
  cleanerId: recordIdSchema,
  /** The first-named cleaner on a visit with several. */
  isLead: z.boolean(),
})

export const jobSchema = z
  .object({
    id: recordIdSchema,
    /** Defaults to the location's name. */
    name: z.string().trim().min(1, 'Give the job a name.').max(200),
    locationId: recordIdSchema,
    recurringJobId: recordIdSchema.nullable().default(null),
    /**
     * The projected day ('YYYY-MM-DD', business zone) this job stands for when a
     * recurring job made it — kept when the visit moves, so the rule never
     * projects that day again (DATA-MODEL-V1 §3). Null exactly when
     * `recurringJobId` is.
     */
    occurrenceOn: z.iso.date().nullable().default(null),
    status: z.enum(jobStatuses),
    scheduledAt: z.iso.datetime(),
    /** The on-site window, when the visit has one ('HH:mm' in the business's zone). */
    startTime: clock.nullable().default(null),
    endTime: clock.nullable().default(null),
    hours: z.number().min(0).max(24).multipleOf(0.25).nullable().default(null),
    /** What this one visit bills. Null when a monthly rate covers it. */
    billAmount: z.number().min(0).nullable().default(null),
    notes: z.string().max(4000).default(''),
    /** The customer gets the day-before notice. */
    notifyCustomer: z.boolean(),
    /** Tell the cleaners — about the job when it's made and the day before. On unless turned off. */
    notifyCleaners: z.boolean().default(true),
    completedAt: z.iso.datetime().nullable().default(null),
    /**
     * Lead first. The forms always ask for one; the record may hold none — an
     * imported visit nobody was on reads "Needs cover" (D-037).
     */
    cleaners: z.array(jobCleanerSchema),
    workTypeIds: z.array(recordIdSchema).min(1),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    /** Who added it; null when an automation did (a recurring job). */
    createdBy: z.object({ id: z.string(), name: z.string() }).nullable().default(null),
    deletedAt: z.iso.datetime().nullable().default(null),
  })
  .refine((job) => (job.recurringJobId === null) === (job.occurrenceOn === null), {
    message: 'A job a recurring job made says which visit it is; no other job does.',
    path: ['occurrenceOn'],
  })
export type Job = z.infer<typeof jobSchema>

/**
 * The job form — the Edit job and New job popups. Date and time are the
 * business's wall clock; the server turns them into `scheduledAt`.
 */
export const jobFormSchema = z.object({
  name: z.string().trim().min(1, 'Give the job a name.').max(200),
  date: z.iso.date({ message: 'Pick a date.' }),
  time: clock,
  workTypeId: recordIdSchema,
  hours: z.number().min(0.25, 'At least a quarter hour.').max(24).multipleOf(0.25).nullable(),
  billAmount: z.number().min(0).nullable(),
  /** The lead cleaner. Anyone else already on the job stays on it. */
  cleanerId: recordIdSchema,
  notifyCustomer: z.boolean(),
  /** Tell the cleaners — about the job when it's made and the day before. On unless turned off. */
  notifyCleaners: z.boolean().default(true),
  notes: z.string().max(4000),
})
export type JobForm = z.infer<typeof jobFormSchema>

export const createJobSchema = jobFormSchema.extend({ locationId: recordIdSchema })
export type CreateJobInput = z.infer<typeof createJobSchema>

export const updateJobSchema = patchOf(jobFormSchema).extend({
  /** Cancel, or put back what Cancel changed (the Undo). */
  status: z.enum(jobStatuses).optional(),
})
export type UpdateJobInput = z.infer<typeof updateJobSchema>

export const recurringStatuses = ['active', 'inactive'] as const

export const recurringJobSchema = z.object({
  id: recordIdSchema,
  name: z.string().min(1).max(200),
  locationId: recordIdSchema,
  /** Only active rules create jobs — the on/off switch. */
  status: z.enum(recurringStatuses),
  cadence: z.enum(cadences),
  /**
   * The series' first visit ('YYYY-MM-DD', business zone): every step counts
   * from it, and a monthly rule keeps its day of month (DATA-MODEL-V1 §3).
   */
  startsOn: z.iso.date(),
  /** The visits' wall-clock start in the business's zone, 'HH:mm' — DST never moves it. */
  startTime: clock,
  /** The last visit day already made into a job by the automation; null before any. */
  materialisedThrough: z.iso.date().nullable().default(null),
  /**
   * Derived, never stored: the first visit after `materialisedThrough` (else
   * `startsOn`) at `startTime`. The repository fills it on every read
   * (`nextVisitAt`) so screens keep reading it.
   */
  nextScheduledAt: z.iso.datetime(),
  /** How many days ahead each visit becomes a real job. */
  leadDays: z.number().int().min(0),
  workTypeId: recordIdSchema,
  hours: z.number().min(0).max(24).nullable().default(null),
  billAmount: z.number().min(0).nullable().default(null),
  notes: z.string().default(''),
  notifyCustomer: z.boolean(),
  /** Tell the cleaners — about the job when it's made and the day before. On unless turned off. */
  notifyCleaners: z.boolean().default(true),
  endDate: z.iso.date().nullable().default(null),
  cleaners: z.array(jobCleanerSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable().default(null),
})
export type RecurringJob = z.infer<typeof recurringJobSchema>

/** A lookup the business edits: inactive types stay on old jobs but leave the menus. */
export const workTypeSchema = z.object({
  id: recordIdSchema,
  name: z.string().min(1),
  active: z.boolean(),
  sortOrder: z.number().int(),
})
export type WorkType = z.infer<typeof workTypeSchema>

/** New recurring job and Edit recurring job: the next visit's day and time are the business's wall clock. */
export const recurringFormSchema = z.object({
  name: z.string().trim().min(1, 'Give it a name.').max(200),
  cadence: z.enum(cadences),
  date: z.iso.date({ message: 'Pick the next visit’s day.' }),
  time: clock,
  workTypeId: recordIdSchema,
  hours: z.number().min(0.25, 'At least a quarter hour.').max(24).multipleOf(0.25).nullable(),
  billAmount: z.number().min(0).nullable(),
  /** The lead cleaner, or nobody yet — its visits then say "Needs cover" (D-034). */
  cleanerId: recordIdSchema.nullable(),
  notifyCustomer: z.boolean(),
  /** Tell the cleaners — about the job when it's made and the day before. On unless turned off. */
  notifyCleaners: z.boolean().default(true),
  /**
   * How many days ahead each visit becomes a real job. Not on the form (D-034):
   * a new one takes `DEFAULT_LEAD_DAYS`, an edit keeps what the rule had.
   */
  leadDays: z.number().int().min(0).max(120).optional(),
  notes: z.string().max(4000),
})
export type RecurringForm = z.infer<typeof recurringFormSchema>

/** How many days ahead a new recurring job's visit becomes a job. */
export const DEFAULT_LEAD_DAYS = 3
